// Las tres cards de referencia en ESCRITORIO: "Añadir producto", "Añadir modelo",
// "Subir imagen" — ADR-2026-09-13-las-cards-de-referencia-de-escritorio-vuelven-como-pieza-de-los-modos.
//
// Vivían adentro de `AccionCardsRow`, que el 2026-09-09 pasó a ser SOLO mobile: el
// asistente movió esas tres acciones al menú "+" de su composer y dejó el empty state de
// escritorio limpio (ADR-2026-09-08-asistente-v2-sin-aura-ni-cards). Esa decisión es del
// asistente y sigue en pie — pero los otros dos modos que compartían la pieza no tienen
// "+" donde caer:
//
//   • Prompt libre (desktop) las dibuja en su panel de configuración, bajo el rótulo
//     "Referencias (opcional)". Su caja no tiene "+".
//   • Creador de video (desktop, estado vacío) las dibuja debajo de la caja. Su "+" sólo
//     aparece con `activo = isMdUp && !!columnaResultados`, o sea DESPUÉS de generar.
//
// Sin ellas, esas dos pantallas se quedaron sin ninguna forma de adjuntar en escritorio.
// Así que las cards vuelven como pieza PROPIA de los modos, en vez de volver adentro de
// la pieza del asistente detrás de un flag: son dos dueños con necesidades distintas, y
// la historia de este componente es precisamente la de un compartido que cambió para uno
// y rompió a los otros dos en silencio.
//
// El markup es el que se borró, tal cual: grilla de 3, `AccionCard` (la misma card que
// usan los pickers), alto 104 y "Subiendo…" mientras sube la imagen.

import React from 'react';
import { Package, Upload, User } from 'lucide-react';
import { AccionCard } from '../../components/AccionCard';
import { palabraDeModeloActual } from '../../../../lib/nombreDeModelo';

export interface CardsDeReferenciaProps {
  onAddProduct: () => void;
  onAddModel: () => void;
  onUploadImage: () => void;
  /** Hay una imagen subiendo: la card de imagen muestra spinner y se apaga. */
  uploading?: boolean;
  /** Ancho de la grilla. El default alinea con la caja del prompt del asistente. */
  className?: string;
}

export const CardsDeReferencia: React.FC<CardsDeReferenciaProps> = ({
  onAddProduct,
  onAddModel,
  onUploadImage,
  uploading = false,
  className = 'w-full max-w-[680px]',
}) => {
  const cards = [
    {
      key: 'product',
      label: 'Añadir producto',
      icon: <Package className="w-5 h-5" strokeWidth={2.25} />,
      onClick: onAddProduct,
      loading: false,
    },
    {
      key: 'model',
      label: `Añadir ${palabraDeModeloActual().singular}`,
      icon: <User className="w-5 h-5" strokeWidth={2.25} />,
      onClick: onAddModel,
      loading: false,
    },
    {
      key: 'image',
      label: 'Subir imagen',
      icon: <Upload className="w-5 h-5" strokeWidth={2.25} />,
      onClick: onUploadImage,
      loading: uploading,
    },
  ];

  // `hidden md:grid`: en mobile la entrada a referencias es la card única de
  // `AccionCardsRow`, que además muestra lo ya elegido. Las dos se montan juntas y se
  // turnan por breakpoint, como venía siendo.
  return (
    <div className={`hidden md:grid grid-cols-3 gap-3 ${className}`}>
      {cards.map((c) => (
        <AccionCard
          key={c.key}
          className="h-[104px]"
          label={c.label}
          icon={c.icon}
          onClick={c.onClick}
          loading={c.loading}
          loadingLabel="Subiendo…"
        />
      ))}
    </div>
  );
};

export default CardsDeReferencia;
